import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  type DocumentData,
  type QueryDocumentSnapshot
} from "firebase/firestore";
import { firestore } from "@/config/firebase";
import type {
  NotificationActionStatus,
  NotificationKind,
  UserNotification
} from "@/features/notifications/types";

const fallbackKind: NotificationKind = "project_update";

function toNotification(snapshot: QueryDocumentSnapshot<DocumentData>): UserNotification {
  const data = snapshot.data();
  const rawActionStatus = data.actionStatus as unknown;
  const actionStatus: NotificationActionStatus | undefined =
    rawActionStatus === "pending" ||
    rawActionStatus === "accepted" ||
    rawActionStatus === "declined" ||
    rawActionStatus === "done"
      ? rawActionStatus
      : undefined;

  return {
    id: snapshot.id,
    kind: typeof data.kind === "string" ? (data.kind as NotificationKind) : fallbackKind,
    title: typeof data.title === "string" ? data.title : "Atualização",
    message: typeof data.message === "string" ? data.message : "Há uma nova atividade para revisar.",
    summary: typeof data.summary === "string" ? data.summary : undefined,
    status: data.status === "read" ? "read" : "unread",
    actionStatus,
    projectId: typeof data.projectId === "string" ? data.projectId : undefined,
    teamId: typeof data.teamId === "string" ? data.teamId : undefined,
    ticketId: typeof data.ticketId === "string" ? data.ticketId : undefined,
    invitationId: typeof data.invitationId === "string" ? data.invitationId : undefined,
    actionUrl: typeof data.actionUrl === "string" ? data.actionUrl : undefined,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    readAt: data.readAt
  };
}

function userNotificationsRef(uid: string) {
  return collection(firestore, "users", uid, "notifications");
}

export function subscribeToUserNotifications(
  uid: string,
  onChange: (notifications: UserNotification[]) => void,
  onError: (error: Error) => void
) {
  const notificationsQuery = query(
    userNotificationsRef(uid),
    orderBy("createdAt", "desc"),
    limit(200)
  );

  return onSnapshot(
    notificationsQuery,
    (snapshot) => onChange(snapshot.docs.map(toNotification)),
    (error) => onError(error)
  );
}

export async function markNotificationRead(uid: string, notificationId: string) {
  await updateDoc(doc(firestore, "users", uid, "notifications", notificationId), {
    status: "read",
    readAt: serverTimestamp()
  });
}
